import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
    ChevronDown, ExternalLink, Video, CheckCircle, XCircle,
    User, RefreshCw,
} from 'lucide-react';
import type { AcademyCreatorItem } from '../types';
import { STATUS_MAP, shortDate } from '../constants';

interface AcademyCreatorCardProps {
    creator: AcademyCreatorItem;
    isExpanded: boolean;
    onToggle: () => void;
    onApprove: () => void;
    onReject: () => void;
    onGoToImport?: () => void;
    isActioning: boolean;
}

export function AcademyCreatorCard({
    creator,
    isExpanded,
    onToggle,
    onApprove,
    onReject,
    onGoToImport,
    isActioning,
}: AcademyCreatorCardProps) {
    const statusCfg = STATUS_MAP[creator.status] || STATUS_MAP.pending;
    const displayName = creator.user_display_name || creator.user_email || creator.channel_display_name || 'Sin nombre';
    const isClaimPending = !!(creator as { claimed_at?: string | null }).claimed_at && creator.status === 'pending';
    const createdDate = new Date(creator.created_at);

    return (
        <div
            className={cn(
                'rounded-xl border bg-card transition-all',
                creator.status === 'pending' ? 'border-amber-500/30' : 'border-border',
                isExpanded && 'ring-1 ring-primary/20'
            )}
        >
            <div
                className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={onToggle}
            >
                <div className="w-14 h-14 rounded-full overflow-hidden bg-muted/50 shrink-0 border border-border/50 flex items-center justify-center">
                    {creator.image_url ? (
                        <img
                            src={creator.image_url}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    ) : (
                        <User size={24} className="text-muted-foreground/50" />
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <p className="text-sm font-medium text-foreground truncate max-w-lg">
                            {displayName}
                        </p>
                        <StatusBadge status={statusCfg.label} variant={statusCfg.variant} />
                        {isClaimPending && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20">
                                Reclamado (pendiente aprobación)
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span className="truncate max-w-xs">{creator.youtube_channel_url}</span>
                        <span className="opacity-40">·</span>
                        <span>{creator.videos_count} vídeos</span>
                        <span className="opacity-40">·</span>
                        <span>{shortDate(createdDate)}</span>
                    </div>
                </div>

                <ChevronDown
                    size={16}
                    className={cn(
                        'text-muted-foreground transition-transform shrink-0',
                        isExpanded && 'rotate-180'
                    )}
                />
            </div>

            {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-4">
                    {creator.description && (
                        <div className="rounded-lg bg-muted/30 p-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descripción</p>
                            <p className="text-sm text-foreground whitespace-pre-line">{creator.description}</p>
                        </div>
                    )}

                    {creator.rejection_reason && (
                        <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3">
                            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-1">Razón de Rechazo</p>
                            <p className="text-sm text-foreground">{creator.rejection_reason}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                        <a
                            href={creator.youtube_channel_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            onClick={e => e.stopPropagation()}
                        >
                            <ExternalLink size={14} />
                            Ver canal YouTube
                        </a>

                        {onGoToImport && creator.status === 'approved' && (
                            <button
                                onClick={e => { e.stopPropagation(); onGoToImport(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                                title="Ir a importar vídeos de este canal"
                            >
                                <RefreshCw size={14} />
                                Importar vídeos
                            </button>
                        )}

                        {creator.status !== 'approved' && (
                            <button
                                onClick={e => { e.stopPropagation(); onApprove(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle size={14} />
                                Aprobar
                            </button>
                        )}

                        {creator.status !== 'rejected' && (
                            <button
                                onClick={e => { e.stopPropagation(); onReject(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                            >
                                <XCircle size={14} />
                                Rechazar
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
